import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Alert,
} from 'reactstrap';
import { useUser } from 'context/UserContext';
import { useAlertPlus, useConfirm } from 'hooks';
import Required from 'components/Required';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import UploaderMultiple from 'components/UploaderMultiple';
import HonorariosEntidades from 'components/MatriculateRequest/HonorariosEntidades';
import {
  getAllConsultoriosAsDirector,
  getConsultorioRenovacionRequest,
  putConsultorioRenovacion,
} from '../../features/consultorios/consultoriosAPI';
import {
  ConsultorioCokibaBD,
  ConsultorioRenovacion,
} from 'types/Consultorio';

export default function EditConsultoriosRenovacion() {
  const { id } = useParams<{id:string}>();
  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const history = useHistory();

  const [formValidation, setFormValidation] = useState<
    Partial<Record<keyof ConsultorioRenovacion, boolean>>
  >({});

  const [disableSend, setDisableSend] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const formElement = useRef<HTMLFormElement | null>(null);

  const [consultorios, setConsultorios] = useState<ConsultorioCokibaBD[]>([]);

  const [canUpdateByState, setCanUpdateByState] = useState(false);
  const [canUpdateAttachs, setCanUpdateAttachs] = useState(false);

  const [data, setData] = useState<ConsultorioRenovacion>({
    id: 0,
    state_id: null,
    matriculate_id: null,
    consultorio_id: null,
    numero_gestion: null,
    numero_expediente: null,
    anio_expediente: null,
    numero_habilitacion: null,
    fecha_habilitacion: null,
    created_at: null,
    resolved_at: null,
    deleted_at: null,
    motivo_rechazo: null,
    user_id: null,
    state: null,
    attachs: [],
  });

  useEffect(() => {
    setLoading(true);

    const loadData = async () => {
      try {
        getAllConsultoriosAsDirector()
          .then(response => {
            setConsultorios(response);
          })
          .catch(err => {
            alert(true, err.getMessage() || 'No se puedieron obtener los pedidos del matriculado');
          })
          .finally(() => {
            setLoading(false);
          });

        const renovacionRequest: ConsultorioRenovacion =
          await getConsultorioRenovacionRequest(+id) as ConsultorioRenovacion;
        if (!!renovacionRequest) {
          setData({
            ...renovacionRequest,
          });
        } else {
          throw new Error('No se pudo recuperar la solicitud de consultorio');
        }
      } catch (err) {
        setDisableSend(true);
        alert(
          'Se produjo un error al intentar recuperar información inicial. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('ConsultorioRenovacionCreate', 'Error al cargar datos iniciales');
        console.info(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  useEffect(() => {
    if (data.state_id == 3) {
      setCanUpdateByState(true);
      setCanUpdateAttachs(true);
    } else if (data.state_id == 6) {
      setCanUpdateAttachs(true);
    }
  }, [data.state_id]);

  const handleFormValidation = () => {
    let newFormValidation = {};

    let toValidate = [
      ...Array.from(document.getElementsByClassName('form-control')),
    ] as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required);

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        field.classList.add('is-invalid');

        missing = true;
      } else if (field.inputMode === 'numeric') {
        if (!/^\d+$/.test(field.value)) {
          newFormValidation = {
            ...newFormValidation,
            [field.name]: false,
          };
        }
      } else {
        field.classList.remove('is-invalid');
      }
    });

    if (missing) {
      setFormValidation(newFormValidation);
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de enviar la solicitud.',
      );
      return false;
    }

    return true;
  };

  // Confirmación de envío de Solicitud
  const handleSendForm = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(
      true,
      '¿Estás seguro que deseas enviar esta solicitud de consultorio?',
      'send',
    );
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'send') {
      handleSend();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleSend = async () => {
    setFormValidation({});

    setLoading(true);

    // Preparamos los inputs menos los archivos
    const consultorioRenovacion: Partial<ConsultorioRenovacion> = {};

    if (!formElement.current) return;

    new FormData(formElement.current).forEach((value, key) => {
      // @ts-ignore
      consultorioRenovacion[key as keyof ConsultorioRenovacion] = value.toString() as any;
    });

    // Seteamos el id y consultorio_id
    consultorioRenovacion['id'] = data.id;
    consultorioRenovacion['consultorio_id'] = data.consultorio_id;

    // Preparamos los archivos
    const files: Record<string, File> = {};
    const inputs = Array.from(document.querySelectorAll('input[type="file"]'));
    for (const inputElement of inputs) {
      const input = inputElement as HTMLInputElement;
      if (input.hasAttribute('required') && input.value === '') {
        alert(
          'Faltan subir archivos requeridos para poder enviar la solicitud.',
        );
        setLoading(false);
        return;
      }

      if (input.files) {
        for (let i = 0; i < input.files.length; i++) {
          files[`${input.name}_${i}`] = input.files[i];
        }
      }
    }

    // Enviamos la petición
    await putConsultorioRenovacion(consultorioRenovacion, files)
      .then(res => {
        if (!res || res.status != 'ok') {
          // Error interno
          console.error('ConsultoriosRenovacionEdit', 'Error al enviar la solicitud');
          console.info(res);
          alert(
            'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
          );
          return;
        }

        alert('La solicitud se envió correctamente', () => {
          history.push('/consultorios');
        });
      })
      .catch(err => {
        console.error(
          'ConsultoriosRenovacionEdit',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const { target } = event;

    const name = target.name;

    let value: string | number | boolean | null;
    value = target.value;

    if (target.type === 'checkbox') {
      value = target.checked;
    }

    if (
      typeof value === 'string' &&
      ['consultorio_id'].includes(name)
    ) {
      value = !!parseInt(value) ? parseInt(value) : '';
    }

    // Guardamos el nuevo valor
    setData({
      ...data,
      [name]: value,
    });
  };

  return (
    <div className="content">
      { data.motivo_rechazo
        ?
          <Alert color="warning">
            <Row className="mb-2">
              <Col sm={12}>
                <span className='group-name observations'>
                  Motivo de rechazo
                  <p className="mt-2">
                    { data.motivo_rechazo }
                  </p>
                </span>
              </Col>
            </Row>
          </Alert>
        : null
      }
      <Row>
        <Col sm={12}>
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">Editar Renovación Consultorio</CardTitle>
                </Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => history.push('/consultorios')}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Form innerRef={formElement}>
                <Col sm={12}>
                  <CardTitle>Consultorio</CardTitle>
                </Col>
                <FormGroup>
                  <Row>
                    <Col md="6">
                      <Label for="consultorio_id">
                        Consultorio <Required />
                      </Label>
                      <Input
                        id="consultorio_id"
                        name="consultorio_id"
                        type="select"
                        className="custom-select form-control"
                        value={data.consultorio_id ?? ''}
                        invalid={formValidation.consultorio_id === false}
                        onChange={handleInputChange}
                        disabled={isLoading || !canUpdateByState}
                        required
                      >
                        <option value="">Seleccione una opción...</option>
                        {consultorios?.map(item => {
                          return (
                            <option
                              key={item.id}
                              value={item.id}
                            >
                              {item.nombre}, {item.calle} {item.numero}, {item.localidad?.MTR008_D_LOCALIDAD}
                            </option>
                          );
                        })}
                      </Input>
                    </Col>
                    <Col md="6"></Col>
                  </Row>
                </FormGroup>
                <hr className="my-4" />
                <Col sm={12}>
                  <CardTitle>Adjuntos</CardTitle>
                </Col>
                <Col sm={12}>
                  <p>
                    <u>IMPORTANTE:</u>
                  </p>
                  <ul>
                    <li>
                      Los profesionales mencionados en la Nota Tipo 8, deben estar al día con el pago de la matrícula.
                    </li>
                    <li>
                      En el caso de presentar contrato de comodato, debe adjuntarse la fotocopia simple de la escritura.
                    </li>
                    <li>
                      En el caso de presentar contrato de alquiler o comodato, debe existir una cláusula donde
                      especifique que el objeto del contrato es el funcionamiento de un consultorio de kinesiología.
                    </li>
                    <li>
                      Los contratos para que tengan validez deben poseer las firmas certificadas y un mínimo de 2 años.
                    </li>
                    <li>
                      Las legalizaciones de las fotocopias y/o las certificaciones de firmas, pueden realizarse ante
                      escribano, juez de paz o registro público de comercio.
                    </li>
                  </ul>
                </Col>
                <Col sm={12}>
                  <HonorariosEntidades
                    titulosPagos={[
                      'TASA_MINISTERIO_SALUD_BS_AS',
                      'ARANCEL_INSPECCION_COKIBA',
                      'ARBA_TIMBRADO_EN_BLANCO',
                      'ARBA_TASA_ADMINISTRATIVA',
                    ]}
                  />
                </Col>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="constancia_pagos">
                        Constancia de haber realizado los 4 pagos detallados anteriormente <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="constancia_pagos"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        multiple
                      />
                    </Col>
                    <Col md="6">
                      <Label for="solicitud_habilitacion">
                        Nota Tipo 8 <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="solicitud_habilitacion"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="declaracion_aparatologia">
                        Declaración Jurada de aparatología <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="declaracion_aparatologia"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        accept={[
                          'image/jpeg',
                          'image/png',
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                      />
                    </Col>
                    <Col md="6">
                      <Label for="declaracion_patogenicos">
                        Declaración Jurada de NO generación de residuos patogénicos <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="declaracion_patogenicos"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        accept={[
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="plano_municipal">
                        Plano Municipal aprobado. En caso de ser fotocopia debe estar legalizada <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="plano_municipal"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        accept={[
                          'image/jpeg',
                          'image/png',
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                        multiple
                      />
                    </Col>
                    <Col md="6">
                      <Label for="contrato_alquiler">
                        Contrato de alquiler, contrato de comodato o escritura, a nombre{' '}
                          de quien será el propietario del consultorio <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="contrato_alquiler"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        accept={[
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                        multiple
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="titulos_grado">
                        Títulos de grado <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="titulos_grado"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        multiple
                      />
                    </Col>
                    <Col md="6">
                      <Label for="constancia_cuit">
                        Constancia de CUIT de todos los profesionales de la kinesiología que{' '}
                          se mencionan en la nota de solicitud <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="constancia_cuit"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        multiple
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="croquis_consultorio">
                        Croquis y memoria descriptiva del consultorio o establecimiento, con firma{' '}
                          y sello del director técnico responsable <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="croquis_consultorio"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        accept={[
                          'image/jpeg',
                          'image/png',
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                      />
                    </Col>
                    <Col md="6">
                      <Label for="resolucion_ministerio">
                        Resolución Ministerio <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="resolucion_ministerio"
                        attachs={data.attachs}
                        required
                        canUpdateAttachs={canUpdateAttachs}
                        accept={[
                          'image/jpeg',
                          'image/png',
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="contrato_comodato">
                        Escritura de contrato de comodato
                      </Label><br/>
                      <UploaderMultiple
                        name="contrato_comodato"
                        attachs={data.attachs}
                        required={false}
                        canUpdateAttachs={canUpdateAttachs}
                        accept={[
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                      />
                    </Col>
                    <Col md="6">
                      <Label for="estatuto_societario">
                        Estatuto Societario
                      </Label><br/>
                      <UploaderMultiple
                        name="estatuto_societario"
                        attachs={data.attachs}
                        required={false}
                        canUpdateAttachs={canUpdateAttachs}
                        accept={['application/pdf']}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                { [3, 6].includes(data.state_id || 0)
                  ?
                    <FormGroup>
                      <div className="text-center">
                        <Button
                          type="button"
                          className="btn-round"
                          onClick={handleSendForm}
                          disabled={isLoading || disableSend}
                        >
                          Enviar
                        </Button>
                      </div>
                    </FormGroup>
                  : null
                }
              </Form>
              <AlertModal
                isOpen={showAlert}
                message={message}
                onClose={alertCallback}
              />
              <ConfirmModal
                isOpen={confirm}
                title={confirmTitle}
                action={confirmAction}
                onClose={() => setConfirm(false)}
                onConfirm={handleConfirmation}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
